/**
 * Perps ownership-auth proofs, generated in the browser.
 *
 * This used to POST the witness to perps-ownership-prover-base. The witness
 * is deliberately not a spending capability — the commitment opening (public
 * key + salt) plus one EdDSA-Poseidon signature over one action digest, with
 * the BabyJubJub secret never leaving the client — but shipping it to a host
 * still handed that host the opening and one replayable action authorization.
 * The circuit is small enough to prove locally, so now nothing leaves the
 * browser at all: the proof is produced from the published, hash-pinned
 * circuit artifacts, exactly like the anonymous-handle proofs
 * (./handle-proof.ts).
 */

import { generateProofClientSide, fetchVerificationKey } from "./proving";

const BN254_PRIME =
  21888242871839275222246405745257275088548364400416034343698204186575808495617n;

const CIRCUIT = "perps_ownership_auth";

/**
 * Canonical bytes32 field element. Silently reducing an out-of-field value
 * mod r would prove a statement about a different signal than the caller
 * named, and the vault's verifier rejects non-canonical public inputs
 * outright — so a reduced proof would be dead on arrival anyway. Reject.
 */
function fieldElement(
  raw: string,
  label: string,
): { hex: string; decimal: string } {
  const value = String(raw || "").trim();
  const with0x = value.startsWith("0x") ? value : `0x${value}`;
  if (!/^0x[0-9a-fA-F]{1,64}$/.test(with0x)) {
    throw new Error(`Invalid field element for ${label}`);
  }
  const n = BigInt(with0x);
  if (n >= BN254_PRIME) {
    throw new Error(`${label} is not a canonical BN254 field element`);
  }
  return {
    hex: `0x${n.toString(16).padStart(64, "0")}`,
    decimal: n.toString(10),
  };
}

/**
 * Groth16 proof in the `uint256[8]` layout the vault verifier expects:
 * [pA, pB.x, pB.y, pC], with each G2 coordinate pair swapped from snarkjs
 * order into EIP-197 order — same transform as handle-proof.ts.
 */
function flattenProofToHex(proofJson: unknown): string {
  const groth16 = proofJson as {
    pi_a?: string[];
    pi_b?: string[][];
    pi_c?: string[];
  };
  const flattened = [
    groth16.pi_a?.[0],
    groth16.pi_a?.[1],
    groth16.pi_b?.[0]?.[1],
    groth16.pi_b?.[0]?.[0],
    groth16.pi_b?.[1]?.[1],
    groth16.pi_b?.[1]?.[0],
    groth16.pi_c?.[0],
    groth16.pi_c?.[1],
  ];
  if (flattened.some((v) => v === undefined || v === null)) {
    throw new Error("Ownership proof has an unexpected shape");
  }
  return `0x${flattened
    .map((v) => BigInt(String(v)).toString(16).padStart(64, "0"))
    .join("")}`;
}

export async function generatePerpsOwnershipProof(params: {
  chainId: number;
  publicKeyX: string;
  publicKeyY: string;
  salt: string;
  ownershipCommitment: string;
  actionHash: string;
  signature: { r8x: string; r8y: string; s: string };
}): Promise<string> {
  const ownershipCommitment = fieldElement(
    params.ownershipCommitment,
    "ownership commitment",
  );
  const actionHash = fieldElement(params.actionHash, "action hash");

  const { proof, publicSignals } = await generateProofClientSide(CIRCUIT, {
    ownershipCommitment: ownershipCommitment.decimal,
    actionHash: actionHash.decimal,
    Ax: fieldElement(params.publicKeyX, "public key x").decimal,
    Ay: fieldElement(params.publicKeyY, "public key y").decimal,
    salt: fieldElement(params.salt, "salt").decimal,
    R8x: fieldElement(params.signature.r8x, "signature R8x").decimal,
    R8y: fieldElement(params.signature.r8y, "signature R8y").decimal,
    S: fieldElement(params.signature.s, "signature S").decimal,
  });

  // Deny by default: only hand back a proof that verifies against the pinned
  // verification key for exactly the public inputs the vault will supply, in
  // the order the Solidity verifier packs them.
  if (
    !Array.isArray(publicSignals) ||
    publicSignals.length !== 2 ||
    String(publicSignals[0]) !== ownershipCommitment.decimal ||
    String(publicSignals[1]) !== actionHash.decimal
  ) {
    throw new Error("Public signals do not match the requested action");
  }
  const snarkjs = await import("snarkjs");
  const verificationKey = await fetchVerificationKey(CIRCUIT);
  if (!(await snarkjs.groth16.verify(verificationKey, publicSignals, proof))) {
    throw new Error("Generated ownership proof failed local verification");
  }

  return flattenProofToHex(proof);
}
